import React, { JSX } from 'react';
import LzCModuleChildBase, { LzIModuleChildBase } from './ModuleChildBase';
import { LzTBase } from '@lz/models/commons/Base';
import LzMGeneric from '@lz/models/commons/Generic';
import LzMCrud from "@lz/models/crud/Crud";
import LzMCrudField, { LzNCrudField } from '@lz/models/crud/Field';
import LzMCrudInput, { LzNCrudInput } from '@lz/models/crud/Input';
import LzMCrudListingColumn, { LzNCrudListingColumn } from '@lz/models/crud/ListingColumn';
import LzMCrudListingActionIcon, { LzNCrudListingActionIcon } from '@lz/models/crud/ListingActionIcon';
import LzXCrudListing from "@lz/components/crud/Listing";
import LzXCrudAdd from "@lz/components/crud/Add";
import LzXCrudEdit from "@lz/components/crud/Edit";
import LzXCrudView from "@lz/components/crud/View";
import { lzFPositionObjectElementTo, lzFSortByPosition } from '@lz/services/Utils';
import { lzFKebabToWordCase, lzFSingularize } from '@lz/services/String';
import { LzTThemeColor } from '@lz/app/Theming/types';
import { LzTOpenObject } from '@lz/typings';
import LzXUsersRights from './built-in/System/Users/Rights';
import LzSApp from '@lz/app/App';

export interface LzIEntity extends LzIModuleChildBase {
    /**
     * The model reference of the entity.
     */
    modelRef?: LzTBase,

    /**
     * Config for CRUD if defined.
     */
    crud?: LzMCrud,

    /**
     * Listing component for this entity (optional: will be set to a default component).
     */
    listingComponent?: React.ReactNode,

    /**
     * Listing component screen title (optional: will be auto generated).
     */
    listingScreenTitle?: string,

    /**
     * Show this content above toolbar on listing component (optional).
     * Can be a callable function which will get following object as argument:
     * {
     *  stats: Stats returned from listing call (if any),
     *  records: Records returned from listing,
     * }
     */
    listingContentAboveToolbar?: any,

    /**
     * Show this content above filters on listing component (optional).
     * Can be a callable function which will get following object as argument:
     * {
     *  stats: Stats returned from listing call (if any),
     *  records: Records returned from listing,
     * }
     */
    listingContentAboveFilters?: any,

    /**
     * Show this content above listing on listing component (optional).
     * Can be a callable function which will get following object as argument:
     * {
     *  stats: Stats returned from listing call (if any),
     *  records: Records returned from listing,
     * }
     */
    listingContentAboveListing?: any,

    /**
     * Add component for this entity (optional: will be set to a default component).
     */
    addComponent?: React.ReactNode,

    /**
     * Add component screen title (optional: will be auto generated).
     */
    addScreenTitle?: string,

    /**
     * Edit component for this entity (optional: will be set to a default component).
     */
    editComponent?: React.ReactNode,

    /**
     * Edit component screen title (optional: will be auto generated).
     */
    editScreenTitle?: string,

    /**
     * View component for this entity (optional: will be set to a default component).
     */
    viewComponent?: React.ReactNode,

    /**
     * View component screen title (optional: will be auto generated).
     */
    viewScreenTitle?: string,

    /**
     * Listing object ref.
     */
    listingRef?: React.RefObject<React.ReactNode>,

    /**
     * A function to return initial values for listing filters (optional: will be set to empty object).
     */
    listingGetInitFilters?: (locState?: LzTOpenObject, queryStringVars?: LzTOpenObject) => any,

    /**
     * Callback to return a React node which displays filters (optional: filters icon will not be shown if this is not defined).
     * The argument `data` can be used to get the filter state values.
     * The argument `handleInputChanage(name: string, value: any)` can be used to handle input filters changes.
     * The argument `handleInputChanages(changes: LzTOpenObject)` can be used to handle input filters changes in bulk.
     */
    listingGetFiltersInput?: (
        data: LzTOpenObject,
        handleInputChanage: (name: string, value: any) => void,
        handleInputChanages?: (changes: LzTOpenObject) => void
    ) => React.ReactNode,

    /**
     * Show filters inline instead of the default method, which is modal when clicked icon in toolbar.
     */
    listingShowFiltersInline?: boolean,
}

class LzCEntity extends LzCModuleChildBase implements LzIEntity {
    modelRef: LzTBase;
    crud: LzMCrud;
    listingComponent: React.ReactNode;
    addComponent?: React.ReactNode;
    editComponent?: React.ReactNode;
    viewComponent?: React.ReactNode;
    listingGetInitFilters?: (locState?: LzTOpenObject, queryStringVars?: LzTOpenObject) => any;
    listingGetFiltersInput?: (
        data: LzTOpenObject,
        handleInputChanage: (name: string, value: any) => void,
        handleInputChanages?: (changes: LzTOpenObject) => void
    ) => React.ReactNode;
    listingShowFiltersInline: boolean;
    listingRef: React.RefObject<React.ReactNode>;

    constructor(model?: LzIEntity) {
        super(model);

        this.variation = 'Entity';

        this.modelRef = model?.modelRef || LzMGeneric;
        this.listingGetInitFilters = model?.listingGetInitFilters;
        this.listingGetFiltersInput = model?.listingGetFiltersInput || this.dummyListingGetFiltersInputCallback;
        this.listingShowFiltersInline = !!model?.listingShowFiltersInline;
        this.listingRef = model?.listingRef || React.createRef();

        this.crud = new LzMCrud(model?.crud || {
            ident: this.ident,
            baseRoute: this.fullRoute,
            fields: {
                name: { size: 100, type: 'string', required: true },
                is_active: { type: 'boolean', default: true },
            },
            listingColumns: {
                name: { label: 'Name', sortable: true, alignment: 'left' },
            },
            addFormInputs: {
                name: { label: 'Name' },
                is_active: { label: 'Active', type: 'switch' },
            },
            viewFormFields: {
                name: { label: 'Name' },
                is_active: { label: 'Active', displayFunction: (val) => !!val ? 'Active' : 'In-Active' },
            },
            editFormInputs: {
                name: { label: 'Name' },
                is_active: { label: 'Active', type: 'switch' },
            },
        });

        this.listingComponent = <LzSApp.WrapperTemplate private screenTitle={model?.listingScreenTitle || 'Manage ' + this.name}>
            <LzXCrudListing
                key={this.crud.ident + '-listing'}
                ref={this.listingRef}
                crud={this.crud}
                contentAboveToolbar={model?.listingContentAboveToolbar}
                contentAboveFilters={model?.listingContentAboveFilters}
                contentAboveListing={model?.listingContentAboveListing}
                modelRef={this.modelRef}
                getInitFilters={this.listingGetInitFilters}
                filtersInput={this.listingGetFiltersInput}
                showFiltersInline={this.listingShowFiltersInline}
            />
        </LzSApp.WrapperTemplate>;

        if (this.crud.showAdd) {
            this.addComponent = <LzSApp.WrapperTemplate private screenTitle={model?.addScreenTitle || 'Create ' + this.name}>
                <LzXCrudAdd
                    key={this.crud.ident + '-add'}
                    crud={this.crud}
                    modelRef={this.modelRef}
                />
            </LzSApp.WrapperTemplate>;
        }

        if (this.crud.showEdit) {
            this.editComponent = <LzSApp.WrapperTemplate private screenTitle={model?.editScreenTitle || 'Edit ' + this.name}>
                <LzXCrudEdit
                    key={this.crud.ident + '-edit'}
                    crud={this.crud}
                    modelRef={this.modelRef}
                />
            </LzSApp.WrapperTemplate>;
        }

        if (this.crud.showView) {
            this.viewComponent = <LzSApp.WrapperTemplate private screenTitle={model?.viewScreenTitle || 'View ' + this.name}>
                <LzXCrudView
                    key={this.crud.ident + '-view'}
                    crud={this.crud}
                    modelRef={this.modelRef}
                />
            </LzSApp.WrapperTemplate>;
        }

        // Add a user person rights route which is different from System module's User rights route
        if (typeof this.crud.listingActionIcons['rights'] !== 'undefined') {
            const rightsRoute = this.crud.listingActionIcons['rights'].route;
            // If rights route exist and its not /system/users/<number>/rights
            if (
                typeof rightsRoute !== 'undefined' &&
                rightsRoute !== '' &&
                rightsRoute !== '/system/users/{recordId}/rights' &&
                rightsRoute.indexOf('/rights') > 0
            ) {
                // It means a rights page needs to be defined for this user
                // NOTE: We for now user the same rights page with same backend api call because the `users` table in backend is always the same
                const personRightsPage = <LzSApp.WrapperTemplate private screenTitle={this.name + ' Rights'}>
                    <LzXUsersRights
                        key={this.crud.ident + '-rights'}
                        goBackRoute={rightsRoute.replace('/{recordId}/rights', '')}
                    />
                </LzSApp.WrapperTemplate>;
                this.routes.push({ path: rightsRoute, element: personRightsPage });
            }
        }

        // console.log('LzCEntity constructor', this);
    }

    private dummyListingGetFiltersInputCallback(data: LzTOpenObject, handleInputChanage: (name: string, value: any) => void) {
        return undefined;
    }

    /**
     * Add a field with possible input and view.
     * 
     * @param key The field key.
     * @param field The field specs.
     * @param addFormInput The add form input specs (do not define or set to null if not needed).
     * @param editFormInput The edit form input specs (do not define or set to null if not needed).
     * @param viewFormField The view form field specs (do not define or set to null if not needed).
     * @param listingCol The listing column specs (do not define or set to null if not needed).
     * @param addFormPos The field's position on add form based on the current field set (optional).
     * @param editFormPos The field's position on edit form based on the current field set (optional).
     * @param viewFormPos The field's position on view form based on the current field set (optional).
     * @param listColsPos The column's position on listing based on the current listing columns (optional).
     */
    addField(
        key: string,
        field: LzNCrudField,
        addFormInput?: LzNCrudInput | null,
        editFormInput?: LzNCrudInput | null,
        viewFormField?: LzNCrudInput | null,
        listingCol?: LzNCrudListingColumn | null,
        addFormPos?: number | null,
        editFormPos?: number | null,
        viewFormPos?: number | null,
        listColsPos?: number | null
    ) {
        // Add the field to the fields
        if (typeof this.crud.fields[key] === 'undefined') {
            this.crud.fields[key] = new LzMCrudField(field, key);
        }

        // Add the add form input if it must be added
        if (typeof this.crud.addFormInputs[key] === 'undefined' && typeof addFormInput !== 'undefined' && addFormInput !== null) {
            this.crud.addFormInputs[key] = new LzMCrudInput(addFormInput, field);
        }

        // Add the edit form input if it must be added
        if (typeof this.crud.editFormInputs[key] === 'undefined' && typeof editFormInput !== 'undefined' && editFormInput !== null) {
            this.crud.editFormInputs[key] = new LzMCrudInput(editFormInput, field);
        }

        // Add the view form field if it must be shown
        if (typeof this.crud.viewFormFields[key] === 'undefined' && typeof viewFormField !== 'undefined' && viewFormField !== null) {
            this.crud.viewFormFields[key] = new LzMCrudInput(viewFormField, field, true);
        }

        // Add the listing column if it must be shown
        if (typeof this.crud.listingColumns[key] === 'undefined' && typeof listingCol !== 'undefined' && listingCol !== null) {
            this.crud.listingColumns[key] = new LzMCrudListingColumn(listingCol, field);
        }

        // Apply the sort based on the given indices of the crud forms and listing (if any)
        if (typeof addFormPos !== 'undefined' && addFormInput !== null && addFormPos !== null) {
            this.crud.addFormInputs = lzFPositionObjectElementTo(this.crud.addFormInputs, key, addFormPos);
        }
        if (typeof editFormPos !== 'undefined' && editFormInput !== null && editFormPos !== null) {
            this.crud.editFormInputs = lzFPositionObjectElementTo(this.crud.editFormInputs, key, editFormPos);
        }
        if (typeof viewFormPos !== 'undefined' && viewFormField !== null && viewFormPos !== null) {
            this.crud.viewFormFields = lzFPositionObjectElementTo(this.crud.viewFormFields, key, viewFormPos);
        }
        if (typeof listColsPos !== 'undefined' && listingCol !== null && listColsPos !== null) {
            this.crud.listingColumns = lzFPositionObjectElementTo(this.crud.listingColumns, key, listColsPos);
        }

        // Return the self object for method chaining
        return this;
    }

    /**
     * Add listing icon to the crud.
     * 
     * @param key The listing icon key.
     * @param icon The listing icon object.
     */
    addListingIcon(key: string, icon: LzNCrudListingActionIcon) {
        // Only add if it is not already added to this key
        if (typeof this.crud.listingActionIcons[key] === 'undefined') {
            // Add the icon
            this.crud.listingActionIcons[key] = new LzMCrudListingActionIcon(icon);

            // Sort them
            this.crud.listingActionIcons = lzFSortByPosition(this.crud.listingActionIcons, 'sortOrder');
        }

        // Return the self object for method chaining
        return this;
    }

    /**
     * Add a child link via listing icon on a parent entity.
     * 
     * @param parentKey The parent key that can be used to determine the state id.
     * @param childKey The child key that can be used to build the route.
     * @param baseRoute The base of route.
     * @param title Title text to show on icon.
     * @param icon Icon element.
     * @param color Icon color.
     * @param sortOrder Sort order in the list of icons.
     */
    addChildLink(parentKey: string, childKey: string, baseRoute: string, title: string, icon: JSX.Element, color: LzTThemeColor, sortOrder: number) {
        const parentSpecs = LzCEntity.getParentFieldSpecsFromKey(parentKey);

        this.addListingIcon(childKey, {
            route: baseRoute + '/' + childKey,
            getRouteParams: (rec) => { return { [parentSpecs.idFieldName]: rec.id, [parentSpecs.nameFieldName]: rec.name }; },
            title: title,
            icon: icon,
            color: color,
            sortOrder: sortOrder,
        });

        // Return the self object for method chaining
        return this;
    }

    /**
     * Add entity parent, to show a field on add/edit forms and on top of listing.
     * 
     * @param key The parent entity key, for e.g. categories, sub-categories, products, etc.
     * @param route The parent page route to get back to.
     * @param showFilterOnListing Show the parent field as a filter on listing page (by default it will show as static text if a parent was selected).
     * @param parentTitleField The parent entity `title` field (optional, default: name).
     * @param parentIdField The parent entity `id` field (optional, default: id).
     */
    addParentField(key: string, route: string, showFilterOnListing?: boolean, parentTitleField?: string, parentIdField?: string) {
        const parentSpecs = LzCEntity.getParentFieldSpecsFromKey(key);

        // Add the parent route
        this.crud.parentFieldKey = key;
        this.crud.parentFieldRoute = route;
        this.crud.parentFieldShowFilterOnListing = !!showFilterOnListing;
        // TODO: Maybe add the parent id and name fields to crud as well

        // Add the CRUD field
        this.crud.fields[parentSpecs.idFieldName] = new LzMCrudField({
            type: 'reference',
            refTableName: key,
            refTableIdField: parentIdField || 'id',
            refTableTitleField: parentTitleField || 'name',
        }, parentSpecs.idFieldName);

        // Add the Add form field if required
        if (this.crud.showAdd && typeof this.crud.addFormInputs[parentSpecs.idFieldName] === 'undefined') {
            this.crud.addFormInputs = {
                [parentSpecs.idFieldName]: new LzMCrudInput({
                    type: 'select',
                    label: parentSpecs.label,
                }),
                ...this.crud.addFormInputs
            };
        }

        // Add the View form field if required
        if (this.crud.showView && typeof this.crud.viewFormFields[parentSpecs.idFieldName] === 'undefined') {
            this.crud.viewFormFields = {
                [parentSpecs.idFieldName]: new LzMCrudInput({
                    type: 'static',
                    label: parentSpecs.label,
                    displayFunction: (val, rec) => rec[parentSpecs.nameFieldName],
                }),
                ...this.crud.viewFormFields
            };
        }

        // Add the Edit form field if required
        if (this.crud.showEdit && typeof this.crud.editFormInputs[parentSpecs.idFieldName] === 'undefined') {
            this.crud.editFormInputs = {
                [parentSpecs.idFieldName]: new LzMCrudInput({
                    type: 'static',
                    label: parentSpecs.label,
                    displayFunction: (val, rec) => rec[parentSpecs.nameFieldName],
                }),
                ...this.crud.editFormInputs
            };
        }

        // Pass the route param to pass arround in the crud
        this.crud.getRouteParams = (locState: any) => {
            // Create output object
            let out = {} as LzTOpenObject;

            // Preserve the old function if its defined
            if (typeof this.crud.getRouteParams !== 'undefined') {
                // TODO: FIX: This is causing recursion
                // out = this.crud.getRouteParams(locState);
            }

            // Add the value of this parent field key if it exists in the location state and not already added
            if (locState !== null) {
                if (typeof locState[parentSpecs.idFieldName] !== 'undefined' && typeof out[parentSpecs.idFieldName] === 'undefined') {
                    out[parentSpecs.idFieldName] = locState[parentSpecs.idFieldName];
                }
                if (typeof locState[parentSpecs.nameFieldName] !== 'undefined' && typeof out[parentSpecs.nameFieldName] === 'undefined') {
                    out[parentSpecs.nameFieldName] = locState[parentSpecs.nameFieldName];
                }
            }

            return out;
        };

        // Return the self object for method chaining
        return this;
    }

    static getParentFieldSpecsFromKey(key: string) {
        const singularKey = lzFSingularize(key);

        return {
            idFieldName: singularKey + '_id',
            nameFieldName: singularKey + '_name',
            label: lzFKebabToWordCase(singularKey),
        };
    }
}

export default LzCEntity;
